import { track } from '@hex/shared/tracking';
import {
  Button,
  HelpButton,
  Select,
  SelectItem,
  tokens,
  Typography,
  Item,
  UnderlinedTabs,
  SegmentedRadio,
  SegmentedRadioGroup,
  View, Label,
} from '@sapphire-react/core';
import { NumberField } from '@sapphire-react/lab';
import { Key, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { featureFlags } from '../../constants';
import { useSapphireMedia } from '../../hooks';
import { ResultWhiteBox } from '../../layout';
import {  formatNumber } from '../../utils';
import { AriaOnlyText } from '../AriaOnlyText';
import { BankLogo } from '../BankLogo';
import { DonutChart } from '../DonutChart/DonutChart';
import { Link } from '../Link';
import {
  getInterestPayment,
  getAmortizationAmount,
  getAmortization
} from './utils';

export enum TaxInquiry {
  BeforeTax = '0',
  AfterTax = '1',
}

export enum ResultType {
  BasedOnMarket = '0',
  ByInterestRate = '1',
}

type Props = {
  displayNavigateLoanApplicationButton: boolean;
  rates?: any;
  ltv?: number;
  loanAmount?: number;
  secondaryApplicantExist?: boolean;
  dataTrackingId: string;
};

export function MonthlyLoanCostCard({
  displayNavigateLoanApplicationButton,
  rates,
  ltv,
  loanAmount,
  secondaryApplicantExist,
  dataTrackingId,
}: Props) {
  const [resultType, setResultType] = useState(
    featureFlags.use3MonthInterestRate?ResultType.ByInterestRate: ResultType.BasedOnMarket,
  );
  const [taxInquiry, setTaxInquiry] = useState(TaxInquiry.AfterTax);
  const [selectedInterestRate, setSelectedInterestRate] = useState<number>(0);
  const [exampleInterestRate, setExampleInterestRate] = useState<string | number | undefined>('');
  const exampleInterestRateRef = useRef(exampleInterestRate);

  useEffect(() => {
    exampleInterestRateRef.current = exampleInterestRate;
  }, [exampleInterestRate]);

  useEffect(() => {
    if (!rates) return;

    if (featureFlags.use3MonthInterestRate) {
      setExampleInterestRate(
        typeof rates === 'number' ? rates.toString() : String(rates.ThreeMonths),
      );
      return;
    }

    // Only initialize a default value; do not overwrite a user-entered interest rate.
    if (!exampleInterestRateRef.current) {
      setExampleInterestRate(String(rates.ThreeMonths));
    }
  }, [rates]);

  const { isExtraSmall, isSmall } = useSapphireMedia();
  const { t } = useTranslation();
  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';
  const isFormDataMissing =
    ltv === undefined ||
    !loanAmount ||
    !rates;

  const interestRates = [
    { value: rates?.ThreeMonths, text: '3 mån' },
    { value: rates?.OneYear, text: '1 år' },
    { value: rates?.TwoYears, text: '2 år' },
    { value: rates?.ThreeYears, text: '3 år' },
    { value: rates?.FourYears, text: '4 år' },
    { value: rates?.FiveYears, text: '5 år' },
    { value: rates?.SixYears, text: '6 år' },
    { value: rates?.TenYears, text: '10 år' },
  ];

  const disabled = !rates;

  const interestRate =
    resultType === ResultType.BasedOnMarket
      ? interestRates[selectedInterestRate].value
      : parseFloat(exampleInterestRate?.toString() ?? '');

  const getAmortizationValue = () => {
    if (
      isFormDataMissing ||
      (resultType === ResultType.ByInterestRate &&
        !parseFloat(exampleInterestRate?.toString() ?? ''))
    ) {
      return undefined;
    }

    return getAmortizationAmount(ltv, loanAmount);
  };

  const getInterestPaymentValue = () => {
    if (isFormDataMissing) {
      return undefined;
    }

    return getInterestPayment(
      resultType === ResultType.BasedOnMarket
        ? interestRates[selectedInterestRate].value
        : parseFloat(exampleInterestRate?.toString() ?? ''),
      loanAmount,
      TaxInquiry.BeforeTax === taxInquiry,
      secondaryApplicantExist || false,
    );
  };

  const getAmortizationSubtitle = () =>
    !isFormDataMissing &&
    (resultType === ResultType.ByInterestRate ? !!exampleInterestRate : true)
      ? `${formatNumber(getAmortization(ltv) * 100)} %`
      : '';

  const getInterestPaymentSubtitle = () => {
    if (isFormDataMissing) {
      return '';
    }

    return interestRate ? `${formatNumber(interestRate)} %` : '';
  };

  const chartItems = [
    {
      value: getAmortizationValue(),
      color: tokens.color.backgroundDecorative1.value,
      legendTitle: t(
        'shared:loanDashboard.donutChart.legend.amortization.label',
      ),
      legendSubtitle: getAmortizationSubtitle() ?? '-',
      contextualHelpContent: isFormDataMissing ? undefined : (
        <>
          <Typography.Body size={'sm'}>
            {t(
              'shared:loanDashboard.donutChart.legend.amortization.tooltip.text',
            )}
          </Typography.Body>
          <Typography.Body size={'md'}>
            <Link marginTop={tokens.size.spacingMd}>
              <a
                target={'_blank'}
                rel={'noopener noreferrer'}
                href={
                  'https://danskebank.se/privat/produkter/bolan/amorteringskrav'
                }
              >
                {t(
                  'shared:loanDashboard.donutChart.legend.amortization.tooltip.link',
                )}
              </a>
            </Link>
          </Typography.Body>
        </>
      ),
      testId: 'amortization-value',
    },
    {
      value: getInterestPaymentValue(),
      color: tokens.color.backgroundDecorative3.value,
      legendTitle: t(
        'shared:loanDashboard.donutChart.legend.interestPayment.label',
      ),
      legendSubtitle: getInterestPaymentSubtitle(),
      testId: 'interest-rate-value',
    },
  ];

  const handleResultTypeChange = (value: Key | null) => {
    if (value === ResultType.ByInterestRate) {
      setExampleInterestRate(Number(interestRates[selectedInterestRate].value));
    }

    setResultType(value as ResultType);
  };

  const getChartTotal = () => {
    const total = chartItems.reduce(
      (prev, curr) => prev + (curr.value ?? 0),
      0,
    );

    if (isFormDataMissing || !total) {
      return '- kr';
    }

    return `${formatNumber(total)} kr`;
  };
  const applyButtonText = t('shared:startLoanApplication');
  const getExampleInterestRateValue = () => {
    if (typeof exampleInterestRate === 'number') return exampleInterestRate;

    if (exampleInterestRate) return Number(exampleInterestRate);

    return undefined;
  };

  return (
    <ResultWhiteBox disabled={disabled}>
      {/* for screen reader to announce that result card is now active  */}
      <div aria-live={'polite'} aria-disabled={disabled}>
        {!disabled && (
          <>
            <AriaOnlyText>
              {`Den månatliga betalningen för lånet skulle bli ${getChartTotal()} kr/månad. Av detta belopp är amortering ${getAmortizationValue()} kr vilket motsvarar ${getAmortizationSubtitle()} av lånet. Räntekostnaden skulle bli ${getInterestPaymentValue()} kr och beräknas med ${interestRate} % ränta.`}
            </AriaOnlyText>
            <AriaOnlyText>
              {`Dessa beräkningar är baserade på räntor i nuvarande marknadsläge, räntor efter ränteavdrag samt en bindningstid på ${interestRates[selectedInterestRate].text}. Det finns tre ändringar du kan göra till beräkningarna. Du kan ändra räntan från att vara i nuvarande marknadsläge till en du själv anger, du kan räkna med eller utan ränteavdrag, och du kan ändra bindningstid för lånet.`}
            </AriaOnlyText>
          </>
        )}
      </div>
      <Typography.Heading level={4}>
        {t('shared:loanDashboard.heading')}
      </Typography.Heading>
      {!featureFlags.use3MonthInterestRate && (
        <UnderlinedTabs
          onSelectionChange={handleResultTypeChange}
          data-test-id={'result-type-tabs'}
        >
          <Item
            key={ResultType.BasedOnMarket}
            data-test-id={'result-type-based-on-market'}
            title={t(
              'shared:loanDashboard.inputs.viewResult.optionsB.basedOnMarket',
            )}
          >
            <></>
          </Item>
          <Item
            key={ResultType.ByInterestRate}
            data-test-id={'result-type-by-interest-rate'}
            title={t(
              'shared:loanDashboard.inputs.viewResult.optionsB.byInterestRate',
            )}
          >
            <></>
          </Item>
        </UnderlinedTabs>
      )}
      {resultType === ResultType.BasedOnMarket &&
        !featureFlags.use3MonthInterestRate && (
          <S.SelectWrapper>
            <Select
              value={selectedInterestRate?.toString()}
              label={t('shared:loanDashboard.inputs.interestRate.label1')}
              width={'100%'}
              marginTop={tokens.size.spacingXl}
              isDisabled={disabled}
              data-test-id={'interest-rate'}
              size={inputSize}
              onChange={(value) =>
                setSelectedInterestRate(parseInt(value as string))
              }
              contextualHelp={
                isFormDataMissing ? undefined : (
                  <HelpButton
                    variant={'help'}
                    aria-label={'Hjälp!'}
                    contentBody={
                      <>
                        <Typography.Body size={'sm'}>
                          {t(
                            'shared:loanDashboard.inputs.interestRate.tooltip1.text',
                          )}
                        </Typography.Body>
                        <Typography.Body size={'md'}>
                          <Link marginTop={tokens.size.spacingMd}>
                            <a
                              target={'_blank'}
                              rel={'noreferrer'}
                              href={
                                'https://danskebank.se/privat/guider/kop-och-salj-bostad/kopa-salja-bostad/roerlig-eller-bunden-raenta'
                              }
                            >
                              {t(
                                'shared:loanDashboard.inputs.interestRate.tooltip1.link',
                              )}
                            </a>
                          </Link>
                        </Typography.Body>
                      </>
                    }
                  />
                )
              }
            >
              {interestRates.map((item, index) => (
                <SelectItem
                  data-test-id={`interest-rate-${item.value}`}
                  key={index}
                >{`${item.text} ${
                  item.value ? `- ${formatNumber(item.value)} %` : ''
                }`}</SelectItem>
              ))}
            </Select>
          </S.SelectWrapper>
        )}
      {(resultType === ResultType.ByInterestRate ||
        featureFlags.use3MonthInterestRate) && (
        <>
          <View marginTop={tokens.size.spacingXl} alignSelf={'start'} marginBottom={tokens.size.spacing2xs}>
            <Label
              htmlFor={'example-interest-rate-AmountInput'}
              style={{ display: 'inline' }}
              aria-label={t(
                'shared:loanDashboard.inputs.interestRate.tooltip2.text',
              )}
              contextualHelp={
                isFormDataMissing ? undefined : (
                  <HelpButton
                    variant={'help'}
                    aria-label={'Hjälp!'}
                    contentBody={t(
                      'shared:loanDashboard.inputs.interestRate.tooltip2.text',
                    )}
                  />
                )
              }
            >
              {t('shared:loanDashboard.inputs.interestRate.label2')}
            </Label>
          </View>

          <NumberField
            isDisabled={disabled}
            id={'example-interest-rate-AmountInput'}
            data-test-id={'example-interest-rate-AmountInput'}
            value={getExampleInterestRateValue()}
            width={'100%'}
            alignInputRight
            onChange={(newValue) => setExampleInterestRate(newValue)}
            minValue={0.01}
            maxValue={15}
            defaultValue={undefined}
            size={inputSize}
            affix={{
              postfix: '%',
            }}
          />
        </>
      )}
      <DonutChart
        chartItems={chartItems}
        donutHeight={238}
        donutWidth={238}
        interactive
        donutPreContent={
          <View marginTop={tokens.size.spacingXl}>
            <SegmentedRadioGroup
              size={'md'}
              aria-label={'Label'}
              onChange={(value) => setTaxInquiry(value as TaxInquiry)}
              value={taxInquiry}
            >
              <SegmentedRadio
                value={TaxInquiry.AfterTax}
                data-test-id={'after-tax'}
              >
                {t('shared:loanDashboard.inputs.taxInquiry.afterTax')}
              </SegmentedRadio>
              <SegmentedRadio
                value={TaxInquiry.BeforeTax}
                data-test-id={'before-tax'}
              >
                {t('shared:loanDashboard.inputs.taxInquiry.beforeTax')}
              </SegmentedRadio>
            </SegmentedRadioGroup>
          </View>
        }
        donutContent={
          <>
            <Typography.Heading
              data-test-id={'donut-total'}
              fontSize={24}
              level={4}
            >
              {getChartTotal()}
            </Typography.Heading>
            <Typography.Body
              size={'sm'}
              UNSAFE_style={{
                color: tokens.color.foregroundSecondary.value,
              }}
            >
              {t('shared:loanDashboard.donutChart.monthly')}
            </Typography.Body>
          </>
        }
      />
      {displayNavigateLoanApplicationButton && (
        <Button
          marginTop={tokens.size.spacingXl}
          iconAlign={'right'}
          isDisabled={disabled}
          data-test-id={'apply-for-loan-commitment'}
          onPress={() => {
            track(dataTrackingId);
            window.open(
              'https://danskebank.se/bostad/ansok-om-bolan/',
              '_blank',
              'noopener,noreferrer',
            );
          }}
          icon={
            <S.IconContainer>
              <BankLogo />
            </S.IconContainer>
          }
        >
          {applyButtonText}
        </Button>
      )}
    </ResultWhiteBox>
  );
}

const S = {
  IconContainer: styled.div`
    box-sizing: border-box;
    padding: 1px;
  `,

  RadioWrapper: styled.div`
    display: flex;
    column-gap: 8px;
  `,

  SelectWrapper: styled.div`
    width: 100%;

    label {
      display: inline;
    }
  `,
};
